import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Lock, Wifi, WifiOff, RadioTower, ScanEye, Fingerprint,
  ShieldAlert, CheckCircle2, XCircle, Loader2, FileText, Printer, Users, Play,
} from "lucide-react";
import { endpoints } from "@/lib/api";
import { appendEvent, readChain, hasVoted, markVoted, newVvpatSerial, clearBooth, countBy } from "@/lib/hashChain";

const BOOTH_ID = "BTH-042";

function Chip({ children, tone = "default", testid }) {
  const tones = {
    default: "border-zinc-300 text-zinc-700 bg-white",
    ok: "border-[#138808] text-[#138808] bg-[#f0fbf3]",
    warn: "border-[#EA580C] text-[#EA580C] bg-[#fff5ec]",
    danger: "border-[#DC2626] text-[#DC2626] bg-[#fdecec]",
    ink: "border-zinc-900 text-white bg-zinc-900",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function StageShell({ children, step, sub }) {
  return (
    <div className="min-h-screen bg-white text-zinc-900 flex flex-col">
      <TopBar />
      <div className="flex-1 flex flex-col">
        <div className="border-b border-zinc-200 px-6 md:px-10 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Voter flow</div>
            <div className="font-display font-black text-xl">{step}</div>
          </div>
          {sub && <div className="text-xs font-mono text-zinc-500">{sub}</div>}
        </div>
        <div className="flex-1 grid place-items-center px-6 py-10">
          <div className="w-full max-w-3xl">{children}</div>
        </div>
      </div>
    </div>
  );
}

function TopBar() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true), dn = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", dn);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", dn); };
  }, []);
  return (
    <>
      <div className="w-full h-1 tricolor-strip" />
      <div className="border-b border-zinc-200 px-6 md:px-10 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-zinc-500 hover:text-zinc-900" data-testid="polling-back">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="w-8 h-8 border border-zinc-900 grid place-items-center"><Lock className="w-3.5 h-3.5" /></div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Polling Kiosk</div>
            <div className="font-display font-bold text-sm leading-none">Booth 042 · Anandpur SAC</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Chip tone={online ? "ok" : "warn"} testid="net-indicator">
            {online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />} {online ? "SYNC-CAPABLE" : "OFFLINE-OK"}
          </Chip>
          <Chip tone="ink"><RadioTower className="w-3 h-3" />DEVICE EVM-2K26-042-A</Chip>
        </div>
      </div>
    </>
  );
}

export default function PollingDevice() {
  const [stage, setStage] = useState("gate"); // gate|roll|scan|fallback|ballot|confirm|vvpat|done|closed
  const [config, setConfig] = useState(null);
  const [voters, setVoters] = useState([]);
  const [snapshotHash, setSnapshotHash] = useState("");
  const [voter, setVoter] = useState(null);
  const [scanResult, setScanResult] = useState(null); // success|false_match|poor_image|outage
  const [choice, setChoice] = useState(null);
  const [slip, setSlip] = useState(null);
  const [booth, setBooth] = useState(null);
  const [officerId, setOfficerId] = useState("OFF-01");
  const [observerId, setObserverId] = useState("OBS-01");
  const [officerPin, setOfficerPin] = useState("");
  const [observerPin, setObserverPin] = useState("");
  const [chainCount, setChainCount] = useState(readChain(BOOTH_ID).length);
  const [fallbackReason, setFallbackReason] = useState("");
  const [fbOfficerPin, setFbOfficerPin] = useState("");
  const [fbObserverPin, setFbObserverPin] = useState("");

  useEffect(() => {
    endpoints.config().then(setConfig).catch(() => toast.error("Config load failed — running with cached snapshot"));
    endpoints.roll(BOOTH_ID)
      .then((r) => { setVoters(r.voters); setSnapshotHash(r.snapshot_hash); setBooth(r.booth); })
      .catch(() => toast.error("Roll load failed"));
  }, []);

  useEffect(() => { setChainCount(readChain(BOOTH_ID).length); }, [stage]);

  const candidates = config?.candidates || [];

  async function handleOpenBooth() {
    try {
      await endpoints.openBooth({ booth_id: BOOTH_ID, officer_id: officerId, observer_id: observerId, officer_pin: officerPin, observer_pin: observerPin });
      await appendEvent(BOOTH_ID, "booth.open", { officer_id: officerId, observer_id: observerId, snapshot_hash: snapshotHash });
      toast.success("Booth opened under dual control");
      setStage("roll");
    } catch (e) {
      toast.error("Officer/Observer credentials rejected");
    }
  }

  function pickVoter(v) {
    if (hasVoted(BOOTH_ID, v.voter_id)) {
      toast.error("This voter has already cast a ballot at this booth");
      return;
    }
    setVoter(v);
    setScanResult(null);
    setStage("scan");
  }

  async function simulateScan(kind) {
    setScanResult({ kind: "scanning" });
    await new Promise((r) => setTimeout(r, 1400));
    await appendEvent(BOOTH_ID, "biometric.attempt", { voter_id: voter.voter_id, outcome: kind });
    setScanResult({ kind });
    if (kind === "success") {
      toast.success("Eligibility verified · one-time token issued");
      setTimeout(() => {
        appendEvent(BOOTH_ID, "auth.token.issued", { voter_id_hash: hashShort(voter.voter_id) });
        // Clear identity context before ballot
        setStage("ballot");
      }, 900);
    } else if (kind === "false_match" || kind === "poor_image" || kind === "outage") {
      toast.warning("Biometric inconclusive — routing to accessibility-safe fallback");
      setStage("fallback");
    }
  }

  async function completeFallback() {
    if (!fallbackReason || fbOfficerPin.length < 4 || fbObserverPin.length < 4) {
      toast.error("Reason + both officer PINs required for fallback");
      return;
    }
    try {
      await endpoints.verifyOfficers({ booth_id: BOOTH_ID, officer_id: officerId, observer_id: observerId, officer_pin: fbOfficerPin, observer_pin: fbObserverPin });
    } catch {
      toast.error("Dual-officer approval failed");
      return;
    }
    await appendEvent(BOOTH_ID, "fallback.approved", { voter_id_hash: hashShort(voter.voter_id), reason: fallbackReason, officer_id: officerId, observer_id: observerId });
    toast.success("Fallback approved — eligibility granted under sealed exception log");
    setStage("ballot");
  }

  async function confirmBallot() {
    const serial = newVvpatSerial(BOOTH_ID);
    // Ballot event is written WITHOUT any voter identity
    await appendEvent(BOOTH_ID, "ballot.cast", { candidate_id: choice.id, vvpat_serial: serial });
    markVoted(BOOTH_ID, voter.voter_id);
    setSlip({ serial, candidate: choice, cast_at: new Date().toISOString() });
    setStage("vvpat");
  }

  async function acceptSlip() {
    await appendEvent(BOOTH_ID, "vvpat.dropped", { vvpat_serial: slip.serial });
    setStage("done");
    toast.success("VVPAT slip deposited · vote recorded");
  }

  async function rejectSlip() {
    await appendEvent(BOOTH_ID, "vvpat.rejected", { vvpat_serial: slip.serial });
    // Report incident
    if (navigator.onLine) {
      try {
        await endpoints.createIncident({ booth_id: BOOTH_ID, kind: "vvpat.mismatch", severity: "high", reported_by: "Presiding Officer", description: `Voter rejected VVPAT slip ${slip.serial}` });
      } catch (_) { /* offline: sync will pick it up later */ }
    }
    toast.error("Slip rejected — incident logged, voter routed to Returning Officer");
    setStage("done");
  }

  function resetVoter() {
    setVoter(null); setChoice(null); setSlip(null); setScanResult(null);
    setFallbackReason(""); setFbOfficerPin(""); setFbObserverPin("");
    setStage("roll");
  }

  async function pushSync() {
    const events = readChain(BOOTH_ID);
    if (!events.length) return toast.info("Nothing to sync");
    try {
      const res = await endpoints.syncEvents({ booth_id: BOOTH_ID, device_signature: "MOCK-SIG-" + BOOTH_ID, events });
      toast.success(`Synced ${res.count} events · ${res.rejected.length} deduped`);
    } catch {
      toast.error("Sync failed — will retry when connectivity returns");
    }
  }

  async function closeBooth() {
    try {
      await endpoints.closeBooth({ booth_id: BOOTH_ID, officer_id: officerId, observer_id: observerId, officer_pin: officerPin || "1234", observer_pin: observerPin || "5678" });
      await appendEvent(BOOTH_ID, "booth.close", { totals: { cast: countBy(BOOTH_ID, "ballot.cast") } });
      await pushSync();
      setStage("closed");
    } catch { toast.error("Booth close requires officer + observer PINs"); }
  }

  function resetDemo() {
    clearBooth(BOOTH_ID); setStage("gate"); setVoter(null); setChoice(null); setSlip(null); setScanResult(null);
    toast.info("Local device chain cleared");
  }

  // Stage components ----------------------------------------------------------
  if (stage === "gate") {
    return (
      <StageShell step="Booth Open Ceremony · Dual Control" sub={`Snapshot ${snapshotHash.slice(0, 12)}…`}>
        <div className="border border-zinc-900 p-8">
          <div className="grid md:grid-cols-2 gap-8">
            <OfficerBox label="Presiding Officer" id={officerId} pin={officerPin} setPin={setOfficerPin} testidPrefix="officer" placeholder="PIN · 1234" />
            <OfficerBox label="Independent Observer" id={observerId} pin={observerPin} setPin={setObserverPin} testidPrefix="observer" placeholder="PIN · 5678" />
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <div className="text-xs font-mono text-zinc-500">Loading electoral-roll snapshot signed by ECI · both PINs required</div>
            <button
              onClick={handleOpenBooth}
              data-testid="btn-open-booth"
              className="bg-[#0A369D] text-white px-6 py-3 font-medium hover:bg-[#08297a]"
            >Open Booth &amp; Load Roll</button>
          </div>
        </div>
        <DevStrip chainCount={chainCount} onSync={pushSync} onReset={resetDemo} />
      </StageShell>
    );
  }

  if (stage === "roll") {
    return (
      <StageShell step="Electoral Roll · Pick Voter" sub={`${voters.length} voters · booth chain ${chainCount} events`}>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {voters.map((v) => {
            const already = hasVoted(BOOTH_ID, v.voter_id);
            return (
              <button
                key={v.voter_id}
                data-testid={`voter-${v.voter_id}`}
                disabled={already}
                onClick={() => pickVoter(v)}
                className={`text-left border p-4 transition-colors ${already ? "opacity-40 cursor-not-allowed border-zinc-200" : "border-zinc-200 hover:border-zinc-900"}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-zinc-300 grid place-items-center font-mono text-sm">{v.photo_hint}</div>
                  <div>
                    <div className="font-medium leading-tight">{v.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{v.voter_id} · Sec {v.section} · {v.age}y</div>
                  </div>
                </div>
                {already && <div className="mt-2 text-[10px] font-mono text-[#138808]">✓ VOTED</div>}
              </button>
            );
          })}
        </div>
        <div className="mt-8 flex flex-wrap gap-3 justify-between">
          <button onClick={closeBooth} data-testid="btn-close-booth" className="border border-zinc-900 px-5 py-2 hover:bg-zinc-50">Close Booth &amp; Seal</button>
          <DevStrip chainCount={chainCount} onSync={pushSync} onReset={resetDemo} inline />
        </div>
      </StageShell>
    );
  }

  if (stage === "scan") {
    return (
      <StageShell step={`Biometric · ${voter.name}`} sub="Retina/Aadhaar adapter · mock only">
        <div className="grid md:grid-cols-[1fr,1fr] gap-6">
          <div className="border border-zinc-900 aspect-square relative overflow-hidden bg-black">
            <div className="absolute inset-0 opacity-70 mix-blend-screen"
              style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1764336222789-28fd1846c6e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800")', backgroundSize: "cover", backgroundPosition: "center" }} />
            <div className="absolute inset-6 border border-[#EA580C]/70" />
            <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-white/80">
              <span className="flex items-center gap-1.5"><ScanEye className="w-3 h-3" /> RETINA SCAN</span>
              <span>{voter.voter_id}</span>
            </div>
            {scanResult?.kind === "scanning" && (
              <motion.div className="scanline" initial={{ top: "10%" }} animate={{ top: "90%" }} transition={{ repeat: Infinity, repeatType: "reverse", duration: 1.1 }} />
            )}
            {scanResult?.kind === "success" && <FlashOverlay color="#138808" icon={CheckCircle2} label="ELIGIBLE" />}
            {(scanResult?.kind === "false_match" || scanResult?.kind === "poor_image" || scanResult?.kind === "outage") && (
              <FlashOverlay color="#EA580C" icon={ShieldAlert} label={scanResult.kind.replace("_", " ").toUpperCase()} />
            )}
          </div>
          <div className="border border-zinc-200 p-6 space-y-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Adapter response · simulate</div>
              <div className="font-display font-bold text-xl">Choose an outcome</div>
            </div>
            <div className="grid grid-cols-1 gap-2">
              <ScanBtn testid="scan-success" onClick={() => simulateScan("success")} color="#138808" icon={CheckCircle2}>Success · Eligible</ScanBtn>
              <ScanBtn testid="scan-false-match" onClick={() => simulateScan("false_match")} color="#EA580C" icon={ShieldAlert}>False match</ScanBtn>
              <ScanBtn testid="scan-poor-image" onClick={() => simulateScan("poor_image")} color="#EA580C" icon={XCircle}>Poor image / injured eye</ScanBtn>
              <ScanBtn testid="scan-outage" onClick={() => simulateScan("outage")} color="#EA580C" icon={WifiOff}>Aadhaar service outage</ScanBtn>
            </div>
            <div className="text-xs font-mono text-zinc-500 leading-relaxed pt-3 border-t border-zinc-200">
              Adapter returns only an eligibility flag. No retina image or Aadhaar
              number is stored. Failed outcomes route to dual-officer fallback,
              never to silent rejection.
            </div>
            <button data-testid="scan-refuse" onClick={() => { setFallbackReason("Voter declined biometric"); setStage("fallback"); }} className="w-full text-sm border border-zinc-300 py-2 hover:bg-zinc-50">Voter refuses biometric →</button>
          </div>
        </div>
      </StageShell>
    );
  }

  if (stage === "fallback") {
    return (
      <StageShell step="Accessibility-safe Fallback · Dual-Officer Approval" sub="Sealed exception log">
        <div className="border border-[#EA580C] p-8 space-y-6">
          <div className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5 text-[#EA580C]" />
            <div className="font-display font-bold text-xl">Non-biometric verification for {voter.name}</div>
          </div>
          <p className="text-sm text-zinc-600 leading-relaxed">The device will not silently reject this voter. Under the ECI Fallback Protocol, both officer and observer authorise identity verification via photo-roll and a legally listed alternate credential.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Documented reason</label>
              <select data-testid="fb-reason" value={fallbackReason} onChange={(e) => setFallbackReason(e.target.value)} className="w-full border border-zinc-300 px-3 py-2 text-sm">
                <option value="">Select reason…</option>
                <option>Biometric outage</option>
                <option>Injured / medical condition</option>
                <option>Elderly / disability</option>
                <option>Voter declined biometric</option>
                <option>False match / mismatch</option>
              </select>
            </div>
            <div />
            <PinField label="Officer PIN" value={fbOfficerPin} setValue={setFbOfficerPin} testid="fb-officer-pin" />
            <PinField label="Observer PIN" value={fbObserverPin} setValue={setFbObserverPin} testid="fb-observer-pin" />
          </div>
          <div className="flex justify-between items-center pt-2">
            <button onClick={() => setStage("scan")} className="text-sm text-zinc-500 hover:text-zinc-900">← Back to scan</button>
            <button data-testid="btn-approve-fallback" onClick={completeFallback} className="bg-zinc-900 text-white px-5 py-3 font-medium hover:bg-zinc-800">Approve &amp; Issue One-Time Token</button>
          </div>
        </div>
      </StageShell>
    );
  }

  if (stage === "ballot") {
    return (
      <StageShell step="Ballot · Select One" sub="Identity context cleared · this screen is anonymous">
        <div className="grid gap-3">
          {candidates.map((c) => (
            <button
              key={c.id}
              data-testid={`candidate-${c.id}`}
              onClick={() => { setChoice(c); setStage("confirm"); }}
              className="flex items-center justify-between border border-zinc-300 hover:border-zinc-900 px-6 py-5 transition-colors"
            >
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 border-2" style={{ borderColor: c.color, color: c.color }} />
                <div className="text-left">
                  <div className="font-display font-bold text-lg">{c.name}</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{c.party} · Symbol: {c.symbol}</div>
                </div>
              </div>
              <div className="w-6 h-6 border-2 border-zinc-900" />
            </button>
          ))}
        </div>
      </StageShell>
    );
  }

  if (stage === "confirm") {
    return (
      <StageShell step="Confirm Selection" sub="Last chance to change your vote">
        <div className="border-2 border-zinc-900 p-8 space-y-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">You are voting for</div>
          <div className="font-display font-black text-4xl">{choice.name}</div>
          <div className="text-sm font-mono text-zinc-600">{choice.party} · {choice.symbol}</div>
          <div className="w-24 h-24 border-4" style={{ borderColor: choice.color }} />
          <div className="flex gap-3 pt-4">
            <button data-testid="btn-change" onClick={() => setStage("ballot")} className="border border-zinc-900 px-6 py-3 hover:bg-zinc-100">Change</button>
            <button data-testid="btn-confirm" onClick={confirmBallot} className="bg-[#138808] text-white px-6 py-3 font-medium hover:bg-[#0f6606]">Cast &amp; Print VVPAT</button>
          </div>
        </div>
      </StageShell>
    );
  }

  if (stage === "vvpat") {
    return (
      <StageShell step="VVPAT Verification Window" sub="7-second sealed voter-visible slip">
        <div className="grid md:grid-cols-[1fr,320px] gap-6 items-start">
          <div className="border border-zinc-300 p-6 bg-zinc-50 overflow-hidden">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Printing behind sealed glass</div>
            <div className="relative h-[360px] overflow-hidden">
              <AnimatePresence>
                <motion.div
                  key={slip.serial}
                  initial={{ y: -320, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 60, damping: 14 }}
                  className="vvpat-slip border border-zinc-400 p-5 shadow-sm"
                >
                  <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest">
                    <span>Anandpur SAC · Booth 042</span>
                    <span>{slip.serial}</span>
                  </div>
                  <div className="mt-4 font-display font-black text-2xl">{slip.candidate.name}</div>
                  <div className="text-sm font-mono text-zinc-700">{slip.candidate.party}</div>
                  <div className="mt-6 flex items-center gap-4">
                    <div className="w-14 h-14 border-2" style={{ borderColor: slip.candidate.color }} />
                    <div className="text-xs font-mono text-zinc-600 leading-relaxed">Symbol: {slip.candidate.symbol}<br />Cast: {new Date(slip.cast_at).toLocaleString()}</div>
                  </div>
                  <div className="mt-6 border-t border-zinc-400 pt-3 flex justify-between text-[10px] font-mono uppercase text-zinc-600">
                    <span>SLIP #{slip.serial.split("-").pop()}</span>
                    <span>NOT A RECEIPT · SECRET BALLOT</span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          <div className="space-y-3">
            <div className="border border-zinc-200 p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Voter, do the slip contents match your choice?</div>
              <div className="font-display font-bold mt-1">Confirm or reject the slip</div>
            </div>
            <button data-testid="btn-slip-accept" onClick={acceptSlip} className="w-full bg-[#138808] text-white py-4 font-medium hover:bg-[#0f6606] flex items-center justify-center gap-2"><Printer className="w-4 h-4" /> Slip Matches · Deposit</button>
            <button data-testid="btn-slip-reject" onClick={rejectSlip} className="w-full border border-[#DC2626] text-[#DC2626] py-4 font-medium hover:bg-[#fdecec] flex items-center justify-center gap-2"><XCircle className="w-4 h-4" /> Slip Mismatch · Report</button>
            <div className="text-xs font-mono text-zinc-500 leading-relaxed">Rejection freezes the electronic totals, opens an incident, and refers the voter to the Returning Officer. No evidence is destroyed.</div>
          </div>
        </div>
      </StageShell>
    );
  }

  if (stage === "done") {
    return (
      <StageShell step="Thank you for voting" sub={`Chain length · ${chainCount}`}>
        <div className="border-2 border-[#138808] p-10 text-center space-y-4">
          <CheckCircle2 className="w-14 h-14 text-[#138808] mx-auto" strokeWidth={1.5} />
          <div className="font-display font-black text-3xl">DHANYAVAAD</div>
          <div className="text-zinc-600 max-w-md mx-auto">Your ballot has been recorded and your paper slip is in the sealed VVPAT compartment. The next voter can now approach the booth.</div>
          <button data-testid="btn-next-voter" onClick={resetVoter} className="mt-4 bg-zinc-900 text-white px-6 py-3 hover:bg-zinc-800">Next Voter</button>
        </div>
      </StageShell>
    );
  }

  if (stage === "closed") {
    return (
      <StageShell step="Booth Sealed" sub="Signed totals exported">
        <div className="border border-zinc-900 p-10 relative overflow-hidden">
          <div className="absolute -right-4 top-4 sealed-stamp font-display font-black text-2xl px-4 py-2">SEALED</div>
          <div className="font-display font-black text-3xl mb-4">Booth {BOOTH_ID} · closed</div>
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            <Stat label="Ballots cast" value={countBy(BOOTH_ID, "ballot.cast")} />
            <Stat label="VVPAT dropped" value={countBy(BOOTH_ID, "vvpat.dropped")} />
            <Stat label="Chain length" value={chainCount} />
          </div>
          <div className="mt-8 text-sm font-mono text-zinc-600 border-t border-zinc-200 pt-4">Event chain synchronised with ECI portal. Physical VVPAT box and device have been sealed under officer + observer signatures.</div>
        </div>
      </StageShell>
    );
  }

  return null;
}

function OfficerBox({ label, id, pin, setPin, testidPrefix, placeholder }) {
  return (
    <div className="border border-zinc-200 p-5 space-y-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display font-bold">{id === "OFF-01" ? "Sarita Deshmukh" : "Colonel V. Menon (Retd.)"}</div>
      <div className="text-xs font-mono text-zinc-500">ID · {id}</div>
      <PinField label="PIN" value={pin} setValue={setPin} testid={`${testidPrefix}-pin`} placeholder={placeholder} />
    </div>
  );
}

function PinField({ label, value, setValue, testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
      <input
        type="password"
        inputMode="numeric"
        maxLength={6}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid={testid}
        placeholder={placeholder || "····"}
        className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 font-mono tracking-widest"
      />
    </div>
  );
}

function ScanBtn({ children, color, icon: Icon, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex items-center gap-3 border border-zinc-200 hover:border-zinc-900 px-4 py-3 text-left">
      <Icon className="w-4 h-4" style={{ color }} />
      <span className="text-sm font-medium">{children}</span>
    </button>
  );
}

function FlashOverlay({ color, icon: Icon, label }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 grid place-items-center bg-black/60">
      <div className="flex flex-col items-center gap-2" style={{ color }}>
        <Icon className="w-14 h-14" strokeWidth={1.5} />
        <div className="font-mono uppercase tracking-widest text-sm">{label}</div>
      </div>
    </motion.div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-zinc-200 p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display font-black text-4xl">{value}</div>
    </div>
  );
}

function DevStrip({ chainCount, onSync, onReset, inline }) {
  return (
    <div className={`${inline ? "" : "mt-8 border-t border-zinc-200 pt-4"} flex flex-wrap items-center gap-3 text-xs font-mono text-zinc-500`}>
      <span>CHAIN · {chainCount}</span>
      <button onClick={onSync} data-testid="btn-sync" className="border border-zinc-300 px-3 py-1 hover:border-zinc-900">Push sync →</button>
      <button onClick={onReset} data-testid="btn-reset-local" className="border border-zinc-300 px-3 py-1 hover:border-zinc-900">Reset local chain</button>
    </div>
  );
}

function hashShort(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return "H" + Math.abs(h).toString(16);
}
